import { Component, OnInit, OnDestroy, ViewChildren, ViewContainerRef, QueryList, ChangeDetectionStrategy } from '@angular/core';
import {
  BaseComponent, MetadataService, User, UserGroup, Features,
  SortDirection, SearchResultsPage, Instrument, TitleService, InstrumentsNavigationKeys, TranslateNotificationKeys,
  RoleAccessService
} from '../../../core';
import { Router, Params, ActivatedRoute } from '@angular/router';
import { first } from 'rxjs/operators';
import { NgxMatToastService } from 'ngx-mat-toast';
import { TranslateService } from '@ngx-translate/core';
import { scrollElementIntoView } from 'src/app/core/utils/scroll';
import { Sort, SortDirection as MatSortDirection } from '@angular/material/sort';
import { PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { ExportFileModalComponent, ExportFileType } from '../../../modals/export-file/export-file-modal/export-file-modal.component';
import { InstrumentAccessService } from '../instrument.access.service';
import { translate } from 'src/app/core/utils/translateServiceHelper';

@Component({
  selector: 'myvirena2-instrument-search',
  templateUrl: './instrument-search.component.html',
  styleUrls: ['./instrument-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class InstrumentSearchComponent extends BaseComponent implements OnInit, OnDestroy {

  private paramSearchText = 'searchText';
  private paramPageNumber = 'pageNumber';
  private paramPageSize = 'pageSize';
  private paramSortColumn = 'sortColumn';
  private paramSortDirection = 'sortDirection';

  public translationBaseKey = 'InstrumentSearch';
  value: string;
  instruments: Instrument[];
  enableAdd: boolean;
  instrumentResults: SearchResultsPage<Instrument>;
  sortColumn = 'serialNumber';
  sortDirection: SortDirection = SortDirection.Ascending;
  sortMatDirection: MatSortDirection;
  userGroup: UserGroup;

  displayColumns: string[] = [];
  pageSize = 20;
  pageNumber = 0;
  sortColumns: Record<string, string> = {
    serialnumber: 'Instrument.SerialNumber',
    facilityname: 'Facility.Name',
    orgname: 'Organization.Name',
    streetaddress1: 'Facility.StreetAddress1',
    city: 'Facility.City',
    state: 'Facility.State.Abbreviation',
    county: 'Facility.County.Name',
    country: 'Facility.Country.Name',
    zipcode: 'Facility.ZipCode'
  };
  ExportFileType = ExportFileType;
  exportButtonsState: string;
  enabledExportButtons: boolean;

  @ViewChildren('matrow', { read: ViewContainerRef }) tableRows: QueryList<ViewContainerRef>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private metadataService: MetadataService,
    private titleService: TitleService,
    private toastr: NgxMatToastService,
    private translate: TranslateService,
    private roleAccessService: RoleAccessService,
    private dialog: MatDialog,
    public accessService: InstrumentAccessService
  ) {
    super();
    this.value = '';
    this.exportButtonsState = 'not-allowed';
    this.enabledExportButtons = false;
  }

  orgAdminCheck(): void {
    this.subscription.add(this.metadataService.getMe().subscribe((me: User) => {
      this.userGroup = <UserGroup>me.group.displayName;

      if (this.userGroup === UserGroup.OrgAdmin) {
        this.displayColumns = ['serialnumber', 'facilityname', 'streetaddress1', 'city', 'state', 'county', 'country', 'zipcode'];
      } else {
        this.displayColumns = ['serialnumber', 'orgname', 'facilityname', 'streetaddress1', 'city', 'state', 'county', 'country', 'zipcode'];
      }
    }));
  }

  doSearch(): void {
    this.pageNumber = 0;
    this.updateData();
  }

  doSort(sortEvent: Sort): void {
    this.sortColumn = sortEvent.active;
    this.sortDirection = sortEvent.direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending;
    this.pageNumber = 0;
    this.updateData();
  }

  doPage(pageEvent: PageEvent): void {

    const isPageUp: boolean = pageEvent.pageIndex >= this.pageNumber;

    this.pageSize = pageEvent.pageSize;
    this.pageNumber = pageEvent.pageIndex;
    this.updateData(true, isPageUp);
  }

  scrollToTop(): void {
    scrollElementIntoView(this.tableRows.first.element);
  }

  scrollToBottom(): void {
    scrollElementIntoView(this.tableRows.last.element);
  }

  updateData(doScroll: boolean = false, isPageUp: boolean = true): void {

    const searchParams: Params = {
      searchText: this.value,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      pageSize: this.pageSize,
      pageNumber: this.pageNumber
    };

    // update navigation
    void this.router.navigate(
      [],
      {
        relativeTo: this.route,
        queryParams: searchParams,
        replaceUrl: true
      }
    );

    this.subscription.add(
      this.metadataService.searchInstruments(this.value,
        this.sortColumns[this.sortColumn.toLowerCase()],
        this.sortDirection,
        this.pageSize,
        +this.pageNumber + 1)
        .subscribe({
          next: (instrumentResults: SearchResultsPage<Instrument>) => {
            this.instrumentResults = instrumentResults;

            if (doScroll) {
              if (isPageUp) {
                this.scrollToTop();
              } else {
                this.scrollToBottom();
              }
            }

            this.enabledExportButtons = this.instrumentResults &&
              this.roleAccessService.getRole() !== UserGroup.TechSupportAdmin &&
              this.roleAccessService.getRole() !== UserGroup.SalesAdmin;

            if (this.enabledExportButtons) {
              this.exportButtonsState = 'pointer';
            } else {
              this.exportButtonsState = 'not-allowed';
            }
          },
          error: () => {
            this.toastr.error(translate(this.translate, `${TranslateNotificationKeys.Prefix}.${TranslateNotificationKeys.SearchFailure}`));
          }
        }));

  }
  createNew(): void {
    void this.router.navigate([InstrumentsNavigationKeys.EditFullPath, 0]);
  }

  onRowSelect(instrument: Instrument): void {
    if (!this.accessService.hasAccessToDetails()) {
      const message = translate(this.translate, `${TranslateNotificationKeys.Prefix}.${TranslateNotificationKeys.InstrumentDetailNoAccessMessage}`);
      this.toastr.error(message);
    }
    else {
      void this.router.navigate([InstrumentsNavigationKeys.EditFullPath, instrument.instrumentId]);
    }
  }

  ngOnInit(): void {
    this.subscription.add(this.route.queryParams.pipe(first()).subscribe(params => {
      this.value = params[this.paramSearchText] as string || '';
      this.pageNumber = params[this.paramPageNumber] as number || this.pageNumber;
      this.pageSize = params[this.paramPageSize] as number || this.pageSize;
      this.sortColumn = (params[this.paramSortColumn] as string || this.sortColumn).toLowerCase();
      this.sortDirection = params[this.paramSortDirection] as SortDirection || this.sortDirection;
      this.sortMatDirection = +this.sortDirection === SortDirection.Descending.valueOf() ? 'desc' : 'asc';

      if (this.pageNumber && this.pageSize && this.sortColumn && this.sortDirection) {
        this.updateData();
      }

      this.checkWriteFlag();
      // OrgAdmin Check
      this.orgAdminCheck();
    }));

    this.titleService.updateTitleTranslateKey(`${this.translationBaseKey}.Title`);
  }

  checkWriteFlag(): void {
    this.subscription.add(this.metadataService.getFeatures().subscribe((features: Features) => {
      this.enableAdd = features.WriteEnabled;

    }));

  }

  ngOnDestroyInternal(): void {
    // Required by base component
  }

  public get hasCreatePermission(): boolean {
    return this.enableAdd && this.userGroup !== UserGroup.OrgAdmin && this.userGroup !== UserGroup.TechSupportAdmin;
  }

  exportFileDialog(fileType: ExportFileType, value: string): void {
    if (!this.enabledExportButtons) {
      return;
    }

    const isTestResultExport = false;
    const modalWidth = 0.27 * window.outerWidth;
    this.dialog.open(ExportFileModalComponent, {
      width: `${modalWidth}px`,
      data: { fileType, value, isTestResultExport }
    });
  }

}
